import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BookOpen, FolderUp, GitBranch } from 'lucide-react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Modal,
  PageHeader,
  Stat,
  Table,
  type Column,
} from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { AgentChip } from '@/modules/agents/components/AgentChip';
import { skillLabel } from '@/modules/agents/components/labels';
import type { AgentIdentity } from '@/modules/agents/utils/agentLabels';
import {
  registryApi,
  type RegistryRole,
  type RegistrySkill,
  type SkillChange,
} from '../api/registryApi';
import { registryKeys } from '../queryKeys';
import { SkillDetailDrawer } from '../components/registry/SkillDetailDrawer';
import { ChangeStatusPill, SkillChangesPanel } from '../components/registry/SkillChangesPanel';
import { SkillWriteBackPanel } from '../components/registry/SkillWriteBackPanel';
import {
  ALL_SETS,
  MODE_LABELS,
  SHARED_OWNER,
  describeTrigger,
  filterBySet,
  registryStats,
} from '../utils/registryView';
import styles from './AgencySkillsRegistryPage.module.css';

type Row = RegistrySkill & { id: string };

function agentIdentities(roles: RegistryRole[]): AgentIdentity[] {
  return roles.map((r) => ({
    roleKey: r.key,
    personaName: r.persona?.name ?? r.displayName,
    displayName: r.displayName,
  }));
}

function RoleCard({ role, agents }: { role: RegistryRole; agents: AgentIdentity[] }) {
  return (
    <Card>
      <div className={styles.roleHead}>
        <AgentChip agentType={role.key} agents={agents} />
        <div className={styles.roleTitle}>
          <div className={styles.roleName}>{role.displayName}</div>
          <div className={styles.rolePath}>agent-suite/src/modules/{role.key}</div>
        </div>
        <Badge variant={role.stage === 'general' ? 'success' : 'warning'} size="sm">
          {role.stage === 'general' ? 'General rollout' : 'Internal only'}
        </Badge>
      </div>
      <p className={styles.roleKpi}>{role.kpi}</p>
      <div className={styles.roleFacts}>
        <span>
          <span className={styles.factLabel}>Skills</span>
          <span className={styles.factValue}>
            {role.counts.live} live
            {role.counts.dormant > 0 ? ` · ${role.counts.dormant} dormant` : ''}
          </span>
        </span>
        <span>
          <span className={styles.factLabel}>Hire from dashboard</span>
          <span className={styles.factValue}>{role.hireable ? 'Yes' : 'Not yet'}</span>
        </span>
        <span>
          <span className={styles.factLabel}>Model</span>
          <span className={`${styles.factValue} ${styles.factMono}`}>
            {role.persona?.model ?? '—'}
          </span>
        </span>
        <span>
          <span className={styles.factLabel}>Mailbox</span>
          <span className={`${styles.factValue} ${styles.factMono}`}>
            {role.persona?.mailbox ?? '—'}
          </span>
        </span>
        <span>
          <span className={styles.factLabel}>Compliance posture</span>
          <span className={styles.factValue}>
            {role.compliancePosture === 'phi' ? 'PHI' : 'Non-PHI'}
          </span>
        </span>
        <span>
          <span className={styles.factLabel}>Evidence policy</span>
          <span className={styles.factValue}>{role.hasEvidencePolicy ? 'Present' : 'Missing'}</span>
        </span>
      </div>
      <div className={styles.roleActions}>
        <Link to={`/agency/agents/flow?role=${encodeURIComponent(role.key)}`}>
          <Button variant="secondary" size="sm">
            Flow map
          </Button>
        </Link>
      </div>
    </Card>
  );
}

export function AgencySkillsRegistryPage() {
  const registry = useQuery({
    queryKey: registryKeys.registry(),
    queryFn: () => registryApi.getRegistry(),
  });
  const [set, setSet] = useState<string>(ALL_SETS);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const writeBack = useQuery({
    queryKey: registryKeys.writeBack(),
    queryFn: () => registryApi.getWriteBack(),
    staleTime: 5 * 60_000,
  });
  const changes = useQuery({
    queryKey: registryKeys.changes({}),
    queryFn: () => registryApi.listChanges({}),
  });
  const changeRows = changes.data;
  const openChangeFor = useMemo(() => {
    const map = new Map<string, SkillChange>();
    for (const c of changeRows ?? []) {
      if (c.status === 'pr_open' && !map.has(`${c.owner}/${c.dirName}`))
        map.set(`${c.owner}/${c.dirName}`, c);
    }
    return map;
  }, [changeRows]);

  const roles = useMemo(() => registry.data?.roles ?? [], [registry.data]);
  const agents = useMemo(() => agentIdentities(roles), [roles]);
  const skills = useMemo(() => registry.data?.skills ?? [], [registry.data]);
  const stats = useMemo(() => registryStats(skills), [skills]);
  const rows = useMemo<Row[]>(
    () => filterBySet(skills, set).map((s) => ({ ...s, id: s.key })),
    [skills, set],
  );
  const hasShared = skills.some((s) => s.owner === SHARED_OWNER);

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'skill',
        header: 'Skill',
        render: (row) => (
          <span>
            <span className={styles.skillName}>{skillLabel(row.key)}</span>
            <span className={styles.skillKey}>{row.key}</span>
          </span>
        ),
      },
      {
        key: 'set',
        header: 'Agent',
        render: (row) =>
          row.owner === SHARED_OWNER ? (
            <span className={patterns.pillMuted}>Shared</span>
          ) : (
            <AgentChip agentType={row.agentType} agents={agents} />
          ),
      },
      {
        key: 'mode',
        header: 'Mode',
        render: (row) => (
          <span className={styles.cellStack}>
            <span className={patterns.pillInfo}>{MODE_LABELS[row.mode]}</span>
            {(row.auditType || row.integration) && (
              <span className={styles.cellSub}>{row.auditType ?? row.integration}</span>
            )}
          </span>
        ),
      },
      {
        key: 'triggers',
        header: 'Runs',
        render: (row) => (
          <span className={styles.cellStack}>
            {row.triggers.length === 0 ? (
              <span className={styles.cellSub}>when routed to</span>
            ) : (
              row.triggers.slice(0, 2).map((t) => (
                <span key={t.key} className={styles.mono}>
                  {describeTrigger(t)}
                </span>
              ))
            )}
            {row.triggers.length > 2 && (
              <span className={styles.cellSub}>+{row.triggers.length - 2} more</span>
            )}
          </span>
        ),
      },
      {
        key: 'format',
        header: 'Format',
        render: (row) => (
          <span className={styles.cellStack}>
            <span className={styles.mono}>
              {row.methodFormat === 'none' ? '—' : row.methodFormat}
            </span>
            {row.referenceCount > 0 && (
              <span className={styles.cellSub}>
                {row.referenceCount} reference{row.referenceCount === 1 ? '' : 's'}
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'evidence',
        header: 'Evidence',
        render: (row) =>
          row.hasEvidencePolicy ? (
            <span className={patterns.pillSuccess}>
              {row.evidenceSubjects.length > 0
                ? `${row.evidenceSubjects.length} subject${row.evidenceSubjects.length === 1 ? '' : 's'}`
                : 'Declared'}
            </span>
          ) : (
            <span className={patterns.pillMuted}>None</span>
          ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => {
          const open = openChangeFor.get(`${row.owner}/${row.dirName}`);
          return (
            <span className={styles.cellStack}>
              {row.status === 'live' ? (
                <span className={patterns.pillSuccess}>Live</span>
              ) : (
                <span className={patterns.pillMuted} title={row.dormantReasons.join('; ')}>
                  Dormant
                </span>
              )}
              {open && <ChangeStatusPill change={open} />}
            </span>
          );
        },
      },
    ],
    [agents, openChangeFor],
  );

  const sets = [
    { id: ALL_SETS, label: 'All sets' },
    ...roles.map((r) => ({ id: r.key, label: r.persona?.name ?? r.displayName })),
    ...(hasShared ? [{ id: SHARED_OWNER, label: 'Shared' }] : []),
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Skills Registry"
        subtitle="Every skill each agent can run, straight from agent-suite's modules on disk. GitHub is the source of truth; edits here become pull requests."
        actions={
          <Button
            size="sm"
            disabled={!writeBack.data?.configured}
            onClick={() => setImporting(true)}
          >
            <FolderUp size={14} aria-hidden="true" /> Import skill
          </Button>
        }
      />

      {registry.isError && (
        <Callout variant="error" title="The registry could not load">
          agent-suite did not answer. Check that it is running and reachable from the backend.
        </Callout>
      )}

      <div className={patterns.statGrid4}>
        <Stat
          label="Skills"
          value={stats.skills}
          sub={`across ${stats.sets} set${stats.sets === 1 ? '' : 's'}`}
        />
        <Stat label="Live" value={stats.live} sub="listed by a role, has a supported tool" />
        <Stat label="Dormant" value={stats.dormant} sub="defined, deliberately unreachable" />
        <Stat
          label="Agents"
          value={roles.length}
          sub={roles.map((r) => r.persona?.name ?? r.displayName).join(' · ') || '—'}
        />
      </div>

      <div className={styles.roles}>
        {roles.map((role) => (
          <RoleCard key={role.key} role={role} agents={agents} />
        ))}
      </div>

      <Card>
        <div className={styles.toolbar}>
          <div className={patterns.filterGroup} role="group" aria-label="Filter by agent set">
            {sets.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`${patterns.filterPill} ${set === s.id ? patterns.filterPillActive : ''}`}
                aria-pressed={set === s.id}
                onClick={() => setSet(s.id)}
              >
                <span className={styles.setPill}>
                  {s.id !== ALL_SETS && s.id !== SHARED_OWNER && (
                    <AgentChip agentType={s.id} agents={agents} compact />
                  )}
                  {s.label}
                </span>
              </button>
            ))}
          </div>
          <span className={patterns.cellMuted}>
            {rows.length} skill{rows.length === 1 ? '' : 's'}
            {set !== ALL_SETS ? ` in ${sets.find((s) => s.id === set)?.label ?? set}` : ''}
          </span>
        </div>
        <Table
          columns={columns}
          data={rows}
          isLoading={registry.isLoading}
          onRowClick={(row) => setOpenKey(row.key)}
          emptyMessage={registry.isError ? 'Nothing to show.' : 'No skills in this set.'}
        />
        <p className={styles.footnote}>
          Click a skill for its manifest, method body and references. A dormant skill is defined on
          disk but left out of its role&apos;s list (or waiting on a tool), so it runs for nobody.
        </p>
      </Card>

      <SkillChangesPanel />

      {writeBack.data?.configured ? (
        <Callout icon={GitBranch} variant="accent" title="How changes land">
          Edit a skill from its drawer, upload a new version, or import a new one. Each change is
          validated by agent-suite first (the same rules the conformance test enforces), then
          committed to its own branch in <code>{writeBack.data.repo}</code> and opened as a pull
          request against <code>{writeBack.data.baseBranch}</code>. A human merges; the row above
          turns green once the merged change is deployed and the agent server restarts and picks it
          up.
        </Callout>
      ) : (
        <Callout icon={BookOpen} variant="accent" title="Adding or changing a skill">
          A skill is a directory in agent-suite: <code>manifest.json</code> plus a method body and
          references. Add it under the role&apos;s module, list its key in <code>role.json</code>,
          and the conformance test is the contract. Write-back from this screen (edit → pull
          request) switches on once the backend has a GitHub App installation configured.
        </Callout>
      )}

      <Modal
        isOpen={importing}
        onClose={() => setImporting(false)}
        title="Import a skill"
        size="lg"
      >
        <SkillWriteBackPanel
          mode="create"
          owner={roles[0]?.key ?? 'shared'}
          dirName={null}
          owners={[...roles.map((r) => r.key), SHARED_OWNER]}
          onCancel={() => setImporting(false)}
        />
      </Modal>

      <SkillDetailDrawer skillKey={openKey} agents={agents} onClose={() => setOpenKey(null)} />
    </div>
  );
}
